import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, TrendUp, TrendDown, Target, Flag, Lightning, Trophy, Crown,
} from "@phosphor-icons/react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

const DIST = [
  { key: "eagle_or_better", label: "Eagle+", color: "#1E5631" },
  { key: "birdie",          label: "Birdie", color: "#3DA35D" },
  { key: "par",             label: "Par",    color: "#0F2537" },
  { key: "bogey",           label: "Bogey",  color: "#E05D3A" },
  { key: "double_plus",     label: "Dbl+",   color: "#A8351B" },
];

export default function AdvancedStats() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/stats/advanced").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="py-10">Loading…</div>;

  const noData = d.rounds === 0;
  const isPro = !!user?.is_pro;

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between rise">
        <Link to="/stats" data-testid="back-to-stats" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
          <ArrowLeft size={14} weight="bold" /> Basic stats
        </Link>
        {!isPro && (
          <Link to="/pricing" data-testid="adv-stats-go-pro" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
            <Crown size={12} weight="fill" className="text-[#E05D3A]" /> Unlock everything with Pro
          </Link>
        )}
      </div>
      <div className="rise">
        <span className="label-eyebrow">Advanced analytics</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Strokes Gained, scrambling, and more</h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-2xl">
          Estimated Strokes Gained metrics calibrated against tour baselines. Putting SG is computed from per-hole putts vs the
          ~1.85 GIR / 1.75 non-GIR PGA Tour average. Total SG ≈ negative differential vs scratch.
        </p>
      </div>

      {noData ? (
        <div className="card-default p-10 text-center">
          <p className="text-sm text-[#0F2537]/60">Play a round to unlock advanced analytics.</p>
          <Link to="/rounds/new" className="inline-flex mt-3 items-center gap-1 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold hover:brightness-110">
            Start a round
          </Link>
        </div>
      ) : (
        <>
          {/* SG row */}
          <div className="grid md:grid-cols-3 gap-4 rise rise-delay-1">
            <SGTile testid="sg-total" label="SG: Total / round" value={d.sg_total_per_round} hint="Strokes gained vs scratch (negative = lost)." />
            <SGTile testid="sg-putting" label="SG: Putting" value={d.sg_putting_per_round} hint="Putts vs tour baseline (positive = you putt better)." />
            <SGTile testid="sg-tee-to-green" label="SG: Tee→Green" value={d.sg_tee_to_green_per_round} hint="Total SG minus putting SG." />
          </div>

          {/* Pro shot-derived SG buckets — only when there are shots tracked. */}
          {d.sg_shot_rounds > 0 && (
            <div className="space-y-2 rise rise-delay-2">
              <div className="flex items-center gap-2">
                <Crown size={14} weight="fill" className="text-[#E05D3A]" />
                <span className="label-eyebrow">Bula Pro · from your tracked shots ({d.sg_shot_rounds} round{d.sg_shot_rounds === 1 ? "" : "s"})</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <SGTile testid="sg-ott" label="SG: Off the Tee" value={d.sg_off_the_tee_per_round} hint="Driver shots on par 4/5 vs tour baseline." />
                <SGTile testid="sg-app" label="SG: Approach" value={d.sg_approach_per_round} hint="Shots from beyond 30 yds (not tee, not putter)." />
                <SGTile testid="sg-arg" label="SG: Around the Green" value={d.sg_around_green_per_round} hint="Shots within 30 yds of the green (not putter)." />
              </div>
            </div>
          )}
          {d.sg_shot_rounds === 0 && isPro && (
            <div className="card-default p-4 flex items-center gap-3 rise rise-delay-2" data-testid="sg-shots-empty">
              <Crown size={16} weight="fill" className="text-[#E05D3A]" />
              <p className="text-sm text-[#0F2537]/80">
                Track a few shots on your next round to unlock <b>SG: Off the Tee / Approach / Around the Green</b>.
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-4 rise rise-delay-2">
            <Tile testid="scrambling" label="Scrambling %" value={d.scrambling_pct == null ? "—" : `${d.scrambling_pct}%`} icon={Target} sub="Saving par after missed GIR" />
            <Tile testid="bounceback" label="Bounceback %" value={d.bounceback_pct == null ? "—" : `${d.bounceback_pct}%`} icon={Lightning} sub="Par-or-better after a bogey" />
            <Tile testid="best-streak" label="Best par streak" value={d.best_streak_par_or_better} icon={Trophy} sub="Consecutive par or better" />
            <Tile testid="rounds-count" label="Rounds analyzed" value={d.rounds} icon={Flag} sub="Across all courses" />
          </div>

          {/* Distribution */}
          <section className="card-default p-5 md:p-7 rise rise-delay-2">
            <span className="label-eyebrow">Hole result distribution</span>
            <h2 className="font-heading font-bold text-xl mt-1 mb-4">Where your strokes go</h2>
            <div className="flex w-full rounded-xl overflow-hidden h-8 mb-4" data-testid="result-bar">
              {DIST.map((b) => {
                const p = d.result_pct?.[b.key] || 0;
                if (p <= 0) return null;
                return (
                  <div key={b.key} style={{ width: `${p}%`, backgroundColor: b.color }}
                    className="flex items-center justify-center text-[10px] font-bold text-white"
                    title={`${b.label}: ${p}%`}
                    data-testid={`result-bar-${b.key}`}>
                    {p >= 8 ? `${p}%` : ""}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {DIST.map((b) => (
                <div key={b.key} className="flex items-center gap-2 text-xs">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: b.color }} />
                  <span className="font-bold">{b.label}</span>
                  <span className="text-[#0F2537]/60 ml-auto">{d.result_distribution?.[b.key] || 0}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Par splits */}
          <section className="card-default p-5 md:p-7 rise rise-delay-3">
            <span className="label-eyebrow">By par type</span>
            <h2 className="font-heading font-bold text-xl mt-1 mb-4">Par 3 / 4 / 5 breakdown</h2>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[420px]">
                <thead className="text-[10px] uppercase tracking-wider text-[#0F2537]/60">
                  <tr>
                    <th className="text-left px-2 py-2">Par</th>
                    <th className="text-right px-2 py-2">Holes</th>
                    <th className="text-right px-2 py-2">Avg</th>
                    <th className="text-right px-2 py-2">Vs par</th>
                    <th className="text-right px-2 py-2">GIR</th>
                    <th className="text-right px-2 py-2">Fairway</th>
                  </tr>
                </thead>
                <tbody>
                  {[3, 4, 5].map((p) => {
                    const row = d.par_splits?.[`par_${p}`];
                    if (!row) return null;
                    return (
                      <tr key={p} className="border-t border-[#E2E0D8]" data-testid={`par-row-${p}`}>
                        <td className="px-2 py-2 font-bold">Par {p}</td>
                        <td className="px-2 py-2 text-right">{row.n}</td>
                        <td className="px-2 py-2 text-right font-bold">{row.avg_score ?? "—"}</td>
                        <td className={`px-2 py-2 text-right font-bold ${row.vs_par > 0 ? "text-[#E05D3A]" : row.vs_par < 0 ? "text-[#1E5631]" : ""}`}>
                          {row.vs_par > 0 ? "+" : ""}{row.vs_par ?? "—"}
                        </td>
                        <td className="px-2 py-2 text-right">{row.gir_pct == null ? "—" : `${row.gir_pct}%`}</td>
                        <td className="px-2 py-2 text-right">{p === 3 ? "—" : (row.fairway_pct == null ? "—" : `${row.fairway_pct}%`)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* SG total trend */}
          {d.rolling?.length > 1 && (
            <section className="card-default p-5 md:p-7 rise rise-delay-3">
              <span className="label-eyebrow">SG Total per round</span>
              <h2 className="font-heading font-bold text-xl mt-1 mb-4">Trend</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.rolling.map((r, i) => ({
                    i: i + 1,
                    date: new Date(r.played_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                    sg_total: r.sg_total, sg_putting: r.sg_putting ?? null,
                  }))} margin={{ top: 5, right: 12, left: -10, bottom: 5 }}>
                    <CartesianGrid stroke="#E2E0D8" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#8C949D" fontSize={11} />
                    <YAxis stroke="#8C949D" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0D8" }} />
                    <ReferenceLine y={0} stroke="#8C949D" strokeDasharray="2 2" />
                    <Line type="monotone" dataKey="sg_total" stroke="#0F2537" strokeWidth={3} dot={{ r: 4, fill: "#E05D3A" }} />
                    <Line type="monotone" dataKey="sg_putting" stroke="#1E5631" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 text-xs mt-2 text-[#0F2537]/70">
                <Legend color="#0F2537" label="SG Total" />
                <Legend color="#1E5631" label="SG Putting" />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function SGTile({ label, value, hint, testid }) {
  const v = value == null ? null : Number(value);
  const positive = v != null && v >= 0;
  const Icon = v == null ? Flag : positive ? TrendUp : TrendDown;
  const color = v == null ? "#8C949D" : positive ? "#1E5631" : "#A8351B";
  return (
    <div className="card-default p-5" data-testid={testid}>
      <span className="label-eyebrow">{label}</span>
      <div className="flex items-end gap-2 mt-1">
        <span className="font-heading font-black text-4xl" style={{ color }}>
          {v == null ? "—" : (positive ? "+" : "") + v.toFixed(2)}
        </span>
        <Icon size={22} weight="fill" style={{ color }} className="mb-1.5" />
      </div>
      <p className="text-[11px] text-[#0F2537]/55 mt-2 leading-snug">{hint}</p>
    </div>
  );
}

function Tile({ label, value, icon: Icon, sub, testid }) {
  return (
    <div className="card-default p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="fill" className="text-[#E05D3A]" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
      <p className="text-[11px] text-[#0F2537]/55 mt-1">{sub}</p>
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded" style={{ background: color }} />{label}</span>;
}
